export const ADMIN_ONLY_EMPLOYEE_PAGE_SLUG = 'admin-access';
export const NON_GRANTABLE_EMPLOYEE_PAGE_SLUGS = [
  ADMIN_ONLY_EMPLOYEE_PAGE_SLUG,
  'postManager',
] as const;

/** Keep in lockstep with EMPLOYEE_PAGES. Drift-tested in employeePageRegistry.test.ts. */
export const EMPLOYEE_PAGE_SLUGS = [
  'index',
  'campaigns',
  'campaign-bases',
  'leads',
  'messaging-inbox',
  'bumping-inbox',
  'customers',
  'campaign-conversations',
  'conversations',
  'help-center',
  'postManager',
  'leadpool',
  'company-lists',
  'feature-flags',
  'admin-access',
  'calendar-bookings',
  'audit-inbox',
  'users',
  'signals',
  'file-manager',
  'onboarding-admin',
  'emails',
  'webhooks',
  'slack-channels',
  'slack-poster',
  'external-slack-inbox',
  'individual-chat',
  'response_evaluation',
  'response-gen-eval',
  'lead-simulator',
  'compare-messaging-strategies',
  'drip-message-tagging',
  'drip-messaging-analytics',
  'iteration-timeline',
  'cleaned-variables',
  'readout',
  'dashboards/performance-debugger',
  'dashboards/bump-analytics',
  'dashboards/reignition-analytics',
  'dashboards/bump-candidates',
  'dashboards/cache-status',
  'dashboards/campaign-leadgen-health',
  'dashboards/campaign-performance',
  'dashboards/low-outreach-users',
  'dashboards/linkedin-account-health',
  'dashboards/usage-tracker',
  'weekly-stats',
  'utils/lead-export',
  'utils/campaign-base-leads',
  'utils/lead-reignition',
  'utils/curation-queue',
  'utils/clean-sales-nav-url',
  'utils/company-list-inspector',
  'utils/exit-leads-by-company',
  'utils/export-linkedin-search',
  'utils/linkedin-feature-access-check',
  'utils/lead-update-audit',
  'utils/leadgen-runs',
  'utils/segmented-search-plan',
  'utils/pricing-sheet',
  'utils/ranking-eval',
  'utils/search-utils',
  'utils/usage-tester',
  'utils/mixrank-tester',
  'utils/scheduling-link-test',
  'demo-data-generator',
] as const;

export type EmployeePortalPermissions = {
  hasFullEmployeeAccess: boolean;
  hasPostManagerAccess: boolean;
  hasSalesEmployeeAccess: boolean;
  allowedPages: string[];
};

export function normalizeEmployeeAllowedPages(pages: unknown): string[] {
  if (!Array.isArray(pages)) {
    return [];
  }
  return [
    ...new Set(
      pages
        .filter(
          (page): page is string => typeof page === 'string' && page.length > 0,
        )
        .map((page) => (page === 'faq-builder' ? 'help-center' : page)),
    ),
  ];
}

export function employeeSlugFromPathname(
  pathname: string,
  slugs: readonly string[],
): string | null {
  const prefix = '/employee';
  if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) {
    return null;
  }
  const rest = pathname.slice(prefix.length).replace(/^\//, '');
  if (rest === '') {
    return 'index';
  }
  const matches = slugs.filter(
    (slug) => rest === slug || rest.startsWith(`${slug}/`),
  );
  if (matches.length === 0) {
    return null;
  }
  return matches.reduce((longest, slug) =>
    slug.length > longest.length ? slug : longest,
  );
}

export function canAccessEmployeePage(
  permissions: Pick<
    EmployeePortalPermissions,
    'hasFullEmployeeAccess' | 'allowedPages'
  >,
  slug: string,
): boolean {
  if (permissions.hasFullEmployeeAccess) {
    return true;
  }
  if (slug === ADMIN_ONLY_EMPLOYEE_PAGE_SLUG) {
    return false;
  }
  return permissions.allowedPages.includes(slug);
}

export function canAccessEmployeePath(
  permissions: Pick<
    EmployeePortalPermissions,
    'hasFullEmployeeAccess' | 'allowedPages'
  >,
  pathname: string,
  slugs: readonly string[],
): boolean {
  if (permissions.hasFullEmployeeAccess) {
    return true;
  }
  const slug = employeeSlugFromPathname(pathname, slugs);
  if (!slug) {
    return false;
  }
  return canAccessEmployeePage(permissions, slug);
}

export function hasEmployeePortalAccess(
  permissions: Pick<
    EmployeePortalPermissions,
    'hasFullEmployeeAccess' | 'allowedPages'
  >,
): boolean {
  return (
    permissions.hasFullEmployeeAccess || permissions.allowedPages.length > 0
  );
}

export function employeeHasPageProcedureAccess(
  permissions: Pick<
    EmployeePortalPermissions,
    'hasFullEmployeeAccess' | 'allowedPages'
  >,
  requiredPages: readonly string[],
): boolean {
  if (permissions.hasFullEmployeeAccess) {
    return true;
  }
  return requiredPages.some((page) => permissions.allowedPages.includes(page));
}

export function parseGrantableEmployeePages(
  pages: string[],
  grantableSlugs: ReadonlySet<string>,
): { ok: true; pages: string[] } | { ok: false; invalid: string[] } {
  const unique = normalizeEmployeeAllowedPages(pages);
  const invalid = unique.filter((page) => !grantableSlugs.has(page));
  if (invalid.length > 0) {
    return { ok: false, invalid };
  }
  return { ok: true, pages: unique };
}

export function firstAllowedEmployeePath(
  permissions: EmployeePortalPermissions,
  orderedSlugs: readonly string[],
): string {
  if (permissions.hasFullEmployeeAccess) {
    return '/employee';
  }
  for (const slug of orderedSlugs) {
    if (slug === ADMIN_ONLY_EMPLOYEE_PAGE_SLUG) {
      continue;
    }
    if (permissions.allowedPages.includes(slug)) {
      return slug === 'index' ? '/employee' : `/employee/${slug}`;
    }
  }
  if (permissions.hasSalesEmployeeAccess) {
    return '/employee/sales';
  }
  return '/employee';
}
